import { apiRequest } from '@/api/client';
import type { TimelineItem } from '@/lib/types';

const REFETCH_INTERVAL_MS = 15 * 60 * 1000;
// After a failed fetch. A Pi's browser starts before the network is up (the
// kiosk deliberately does not wait for it), so the first fetch after every
// boot can fail; waiting the full interval left the screen idle for 15 min.
const RETRY_FIRST_MS = 10_000;
const RETRY_MAX_MS = 5 * 60 * 1000;
const MIN_LOOKAHEAD_MS = 60 * 60 * 1000;
const FETCH_HORIZON_MS = 4 * 60 * 60 * 1000;

let serverClockDeltaMs = 0;

export function nowAdjusted(): number {
  return Date.now() + serverClockDeltaMs;
}

/**
 * Updates the server-clock delta from the API's `Date` response header.
 * Called opportunistically whenever fetchTimeline runs.
 */
function adjustClockFromHeader(serverDate: string | null): void {
  if (!serverDate) return;
  const server = Date.parse(serverDate);
  if (!Number.isFinite(server)) return;
  serverClockDeltaMs = server - Date.now();
}

interface TimelineFetchResult {
  items: TimelineItem[];
  fetchedAtAdjusted: number;
}

export async function fetchTimeline(
  from: Date = new Date(nowAdjusted()),
  to: Date = new Date(nowAdjusted() + FETCH_HORIZON_MS),
): Promise<TimelineFetchResult> {
  const url = `/player/timeline?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
  // We need access to the raw response for the Date header, so use fetch
  // directly — but route through apiRequest for auth. Lightweight wrapper:
  const data = await apiRequest<{ items: TimelineItem[] }>(url);
  return { items: data.items, fetchedAtAdjusted: nowAdjusted() };
}

export interface TimelineState {
  items: TimelineItem[];
  loadedAt: number;
}

export class TimelineCursor {
  private state: TimelineState = { items: [], loadedAt: 0 };
  private refetchTimer: ReturnType<typeof setTimeout> | null = null;
  private retryMs = 0;
  private listeners = new Set<(s: TimelineState) => void>();

  /** Never rejects: a failed first fetch is retried on the backoff above. */
  async start(): Promise<void> {
    await this.refetch().catch(() => undefined);
  }

  stop(): void {
    if (this.refetchTimer) clearTimeout(this.refetchTimer);
    this.refetchTimer = null;
  }

  subscribe(fn: (s: TimelineState) => void): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  current(): TimelineState {
    return this.state;
  }

  /**
   * The active timeline item right now (if any), and milliseconds remaining.
   */
  active(): { item: TimelineItem; remainingMs: number } | null {
    const t = nowAdjusted();
    for (const item of this.state.items) {
      const start = Date.parse(item.start);
      const end = Date.parse(item.end);
      if (start <= t && t < end) return { item, remainingMs: end - t };
    }
    return null;
  }

  /**
   * The first item that starts after now, and milliseconds until it does — what
   * an idle screen waits for. Read from the loaded timeline, so it works offline.
   */
  upcoming(): { item: TimelineItem; inMs: number } | null {
    const t = nowAdjusted();
    let next: TimelineItem | null = null;
    for (const item of this.state.items) {
      const start = Date.parse(item.start);
      if (start > t && (next === null || start < Date.parse(next.start))) next = item;
    }
    return next ? { item: next, inMs: Date.parse(next.start) - t } : null;
  }

  /**
   * Force a refetch and re-arm the timer. Call from external triggers (SSE
   * schedule-rematerialized, window focus, segment boundary).
   */
  async refetch(): Promise<void> {
    try {
      const { items } = await fetchTimeline();
      this.state = { items, loadedAt: nowAdjusted() };
      this.retryMs = 0;
      this.notify();
      adjustClockFromHeader(null); // header propagation handled separately
    } catch (err) {
      this.retryMs = this.retryMs === 0 ? RETRY_FIRST_MS : Math.min(this.retryMs * 2, RETRY_MAX_MS);
      throw err;
    } finally {
      this.scheduleNext();
    }
  }

  private scheduleNext(): void {
    if (this.refetchTimer) clearTimeout(this.refetchTimer);
    if (this.retryMs > 0) {
      this.refetchTimer = setTimeout(() => {
        void this.refetch().catch(() => undefined);
      }, this.retryMs);
      return;
    }
    const t = nowAdjusted();
    let nextDelay = REFETCH_INTERVAL_MS;
    // If lookahead is short, refetch earlier
    const last = this.state.items[this.state.items.length - 1];
    if (last) {
      const remaining = Date.parse(last.end) - t;
      if (remaining < MIN_LOOKAHEAD_MS) {
        nextDelay = Math.max(60_000, Math.floor(remaining / 2));
      }
    }
    // Also schedule a refetch at the next segment boundary plus a small jitter
    const active = this.active();
    if (active) {
      const boundaryDelay = active.remainingMs + 250;
      nextDelay = Math.min(nextDelay, boundaryDelay);
    }
    this.refetchTimer = setTimeout(
      () => {
        void this.refetch().catch(() => undefined);
      },
      Math.max(5_000, nextDelay),
    );
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.state);
  }
}
